import { TIERS, type TierKey } from '@commpass/shared';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { CheckIcon, DocIcon } from '../components/Icons';
import { StaffTabs } from '../components/StaffTabs';
import { StatusPill } from '../components/StatusPill';
import { useSubmissions } from '../data/submissions';
import { fmtDate, money, parseMoney } from '../lib/format';
import type { SubmissionRow } from '../types';

const CLOSED = ['bound', 'declined', 'closed_lost'];

export function Dashboard() {
  const navigate = useNavigate();
  const { email } = useAuth();
  const { data, isLoading, error } = useSubmissions();
  const subs = useMemo(() => data ?? [], [data]);

  const m = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isClosed = (s: SubmissionRow) => CLOSED.includes(s.status);
    const active = subs.filter((s) => !isClosed(s) && s.status !== 'invited');
    const needBy = (s: SubmissionRow) => {
      const v = s.data.quoteNeedBy;
      if (!v) return null;
      const d = new Date(String(v));
      return Number.isNaN(d.getTime()) ? null : d;
    };
    const biz = (s: SubmissionRow) => s.business_name || (s.data.businessName as string) || '(Awaiting agent)';

    // Needs attention: overdue > info needed > new intake
    const seen = new Set<string>();
    const attention: { s: SubmissionRow; reason: string; cls: string }[] = [];
    active
      .filter((s) => {
        const d = needBy(s);
        return d && d < today;
      })
      .forEach((s) => {
        if (!seen.has(s.id)) {
          seen.add(s.id);
          attention.push({ s, reason: 'Overdue', cls: 'overdue' });
        }
      });
    subs
      .filter((s) => s.status === 'info_needed')
      .forEach((s) => {
        if (!seen.has(s.id)) {
          seen.add(s.id);
          attention.push({ s, reason: 'Info needed', cls: 'info' });
        }
      });
    subs
      .filter((s) => s.status === 'received')
      .forEach((s) => {
        if (!seen.has(s.id)) {
          seen.add(s.id);
          attention.push({ s, reason: 'New — triage', cls: 'new' });
        }
      });

    const pipelinePremium = active.reduce((a, s) => a + parseMoney(s.data.targetPricing), 0);
    const boundSubs = subs.filter((s) => s.status === 'bound');
    const boundPremium = boundSubs.reduce(
      (a, s) => a + (parseMoney(s.data.proposalPremium) || parseMoney(s.data.targetPricing)),
      0,
    );
    const avgBound = boundSubs.length ? Math.round(boundPremium / boundSubs.length) : 0;

    const declinedCount = subs.filter((s) => s.status === 'declined').length;
    const closedLostCount = subs.filter((s) => s.status === 'closed_lost').length;
    const decided = boundSubs.length + declinedCount + closedLostCount;
    const convRate = decided ? Math.round((boundSubs.length / decided) * 100) : 0;

    const tierCounts = { complete: 0, assist: 0, launch: 0 };
    subs.forEach((s) => {
      if (s.tier === 'complete' || s.tier === 'assist' || s.tier === 'launch') tierCounts[s.tier] += 1;
    });

    const stages: { label: string; match: (s: SubmissionRow) => boolean }[] = [
      { label: 'Received', match: (s) => s.status === 'received' },
      { label: 'In Review', match: (s) => s.status === 'review' },
      { label: 'Quoting / MGA', match: (s) => ['quoting', 'mga_creating', 'mga_sent'].includes(s.status) },
      { label: 'Proposal Sent', match: (s) => s.status === 'proposal_sent' },
      { label: 'Bound', match: (s) => s.status === 'bound' },
    ];
    const stageCounts = stages.map((st) => ({ label: st.label, n: subs.filter(st.match).length }));
    const maxStage = Math.max(1, ...stageCounts.map((x) => x.n));
    const maxTier = Math.max(1, tierCounts.complete, tierCounts.assist, tierCounts.launch);
    const totalDollars = pipelinePremium + boundPremium;

    return {
      active,
      attention,
      pipelinePremium,
      boundSubs,
      boundPremium,
      avgBound,
      declinedCount,
      closedLostCount,
      decided,
      convRate,
      tierCounts,
      stageCounts,
      maxStage,
      maxTier,
      totalDollars,
      invited: subs.filter((s) => s.status === 'invited').length,
      infoNeeded: subs.filter((s) => s.status === 'info_needed').length,
      biz,
      needBy,
    };
  }, [subs]);

  const bar = (label: string, n: number, max: number, orange = false) => (
    <div className="bar-row" key={label}>
      <div className="bar-label">{label}</div>
      <div className="bar-track">
        <div className={`bar-fill${orange ? ' orange' : ''}`} style={{ width: `${Math.round((n / max) * 100)}%` }} />
      </div>
      <div className="bar-val">{n}</div>
    </div>
  );

  const tierBar = (k: 'complete' | 'assist' | 'launch') => (
    <div className="bar-row" key={k}>
      <div className="bar-label">
        {TIERS[k].name} <span style={{ color: 'var(--muted)' }}>{TIERS[k].member}%</span>
      </div>
      <div className="bar-track">
        <div className="bar-fill orange" style={{ width: `${Math.round((m.tierCounts[k] / m.maxTier) * 100)}%` }} />
      </div>
      <div className="bar-val">{m.tierCounts[k]}</div>
    </div>
  );

  return (
    <div className="wrap">
      <StaffTabs />
      <div className="page-head">
        <div>
          <div className="page-title">Program dashboard</div>
          <div className="page-sub">
            COMM-PASS · {email ?? 'AAS Specialist'} · {subs.length} submission{subs.length === 1 ? '' : 's'} to date
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/settings')}>
          <DocIcon width={15} height={15} /> Import members
        </button>
      </div>

      {isLoading && <div className="empty"><p>Loading…</p></div>}
      {error && (
        <div className="empty">
          <h3>Couldn't load</h3>
          <p>{(error as Error).message}</p>
        </div>
      )}

      {!isLoading && !error && (
        <>
          <div className="kpi-grid">
            <div className="kpi">
              <div className="kpi-num">{m.active.length}</div>
              <div className="kpi-label">Open submissions</div>
            </div>
            <div className={`kpi${m.attention.length > 0 ? ' alert' : ''}`}>
              <div className="kpi-num">{m.attention.length}</div>
              <div className="kpi-label">Needs attention</div>
            </div>
            <div className="kpi">
              <div className="kpi-num">{money(m.pipelinePremium)}</div>
              <div className="kpi-label">Pipeline premium</div>
            </div>
            <div className="kpi">
              <div className="kpi-num">{money(m.boundPremium)}</div>
              <div className="kpi-label">Bound premium</div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <h3>Needs attention</h3>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>Overdue, waiting on info, or brand new</span>
            </div>
            <div className="panel-pad" style={{ paddingTop: 6 }}>
              {m.attention.length === 0 ? (
                <div style={{ padding: '14px 0', fontSize: 13.5, color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="ok-check"><CheckIcon width={12} height={12} /></span> All caught up — nothing overdue or waiting.
                </div>
              ) : (
                m.attention.map((a) => (
                  <div className="att-row" key={a.s.id} onClick={() => navigate(`/s/${a.s.id}`)}>
                    <span className={`att-reason ${a.cls}`}>{a.reason}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 13.5 }}>{m.biz(a.s)}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {a.s.id} · {a.s.tier ? TIERS[a.s.tier as TierKey]?.name : '—'}
                        {a.s.data.quoteNeedBy ? ` · need-by ${fmtDate(a.s.data.quoteNeedBy)}` : ''}
                      </div>
                    </div>
                    <StatusPill status={a.s.status} reviewer />
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="panel">
            <div className="panel-head"><h3>Pipeline</h3></div>
            <div className="panel-pad">
              {m.stageCounts.map((x, i) => bar(x.label, x.n, m.maxStage, i === m.stageCounts.length - 1))}
              <div style={{ marginTop: 14, display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--muted)' }}>
                <span>Info needed: <b style={{ color: '#8A5A0E' }}>{m.infoNeeded}</b></span>
                <span>Declined: <b style={{ color: 'var(--red)' }}>{m.declinedCount}</b></span>
                <span>Closed — not bound: <b style={{ color: 'var(--slate)' }}>{m.closedLostCount}</b></span>
                <span>Awaiting agent: <b>{m.invited}</b></span>
              </div>
            </div>
          </div>

          <div className="dash-2col">
            <div className="panel">
              <div className="panel-head"><h3>Dollars</h3></div>
              <div className="panel-pad">
                <div className="bar-row">
                  <div className="bar-label">Pipeline (open)</div>
                  <div className="bar-track">
                    <div className="bar-fill" style={{ width: `${m.totalDollars ? Math.round((m.pipelinePremium / m.totalDollars) * 100) : 0}%` }} />
                  </div>
                  <div className="bar-val" style={{ width: 'auto' }}>{money(m.pipelinePremium)}</div>
                </div>
                <div className="bar-row">
                  <div className="bar-label">Bound</div>
                  <div className="bar-track">
                    <div className="bar-fill orange" style={{ width: `${m.totalDollars ? Math.round((m.boundPremium / m.totalDollars) * 100) : 0}%` }} />
                  </div>
                  <div className="bar-val" style={{ width: 'auto' }}>{money(m.boundPremium)}</div>
                </div>
                <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                  {m.boundSubs.length} account(s) bound · avg {money(m.avgBound)} each. Pipeline premium sums the target pricing on open submissions.
                </div>
              </div>
            </div>

            <div className="panel">
              <div className="panel-head"><h3>Program health</h3></div>
              <div className="panel-pad">
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span style={{ fontFamily: 'Archivo', fontWeight: 800, fontSize: 30, color: 'var(--green)' }}>{m.convRate}%</span>
                  <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                    bind rate · {m.boundSubs.length} bound of {m.decided} decided
                  </span>
                </div>
                <div className="section-label" style={{ marginTop: 18 }}>Tier mix</div>
                {(['complete', 'assist', 'launch'] as const).map((k) => tierBar(k))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
